import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import { Toaster } from "@/components/ui/sonner";

// Existing
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Conversations from "@/pages/Conversations";
import Leads from "@/pages/Leads";
import Tickets from "@/pages/Tickets";
import Documents from "@/pages/Documents";
import DeploymentGuide from "@/pages/DeploymentGuide";
import Widget from "@/pages/Widget";
import OrchidDemo from "@/pages/OrchidDemo";
import Movie, { BRAND_FUNDLE, BRAND_XCEED } from "@/pages/Movie";
import Proposal from "@/pages/Proposal";
import Topics from "@/pages/Topics";
import Sentiment from "@/pages/Sentiment";
import QA from "@/pages/QA";
import Snippets from "@/pages/Snippets";
import Websites from "@/pages/Websites";
import Suggestions from "@/pages/Suggestions";
import Playground from "@/pages/Playground";
import TrainAgent from "@/pages/TrainAgent";
import LegacySettings from "@/pages/Settings";

// New Chatbase-style pages
import AnalyticsChats from "@/pages/AnalyticsChats";
import Actions from "@/pages/Actions";
import Widgets from "@/pages/Widgets";
import Contacts from "@/pages/Contacts";
import Deploy from "@/pages/Deploy";
import Outbound from "@/pages/Outbound";
import DataSourceNotion from "@/pages/DataSourceNotion";
import DataSourceTickets from "@/pages/DataSourceTickets";
import { HelpdeskEmail, HelpdeskAssignment, HelpdeskScheduling, HelpdeskTranslation } from "@/pages/Helpdesk";
import SettingsGeneral from "@/pages/SettingsGeneral";
import SettingsAI from "@/pages/SettingsAI";
import SettingsVoice from "@/pages/SettingsVoice";
import SettingsIntegrations from "@/pages/SettingsIntegrations";
import SettingsFundle from "@/pages/SettingsFundle";
import SettingsLeadEmail from "@/pages/SettingsLeadEmail";
import SettingsAgents from "@/pages/SettingsAgents";
import SettingsCannedReplies from "@/pages/SettingsCannedReplies";
import SettingsUsers from "@/pages/SettingsUsers";
import LiveChats from "@/pages/LiveChats";
import AgentLogin from "@/pages/AgentLogin";
import AgentConsole from "@/pages/AgentConsole";
import SettingsEmails from "@/pages/SettingsEmails";
import SettingsChatInterface from "@/pages/SettingsChatInterface";
import SettingsSecurity from "@/pages/SettingsSecurity";
import SettingsCustomDomains from "@/pages/SettingsCustomDomains";
import SettingsWebhooks from "@/pages/SettingsWebhooks";
import SettingsNotifications from "@/pages/SettingsNotifications";

function AdminRoutes() {
    return (
        <ProtectedRoute>
            <AdminLayout />
        </ProtectedRoute>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors />
                    <Routes>
                        <Route path="/widget" element={<Widget />} />
                        <Route path="/demo" element={<OrchidDemo />} />
                        <Route path="/agent/login" element={<AgentLogin />} />
                        <Route path="/agent" element={<AgentConsole />} />
                        <Route path="/movie" element={<Movie brand={BRAND_FUNDLE} />} />
                        <Route path="/agent1" element={<Movie brand={BRAND_XCEED} />} />
                        <Route path="/proposal" element={<Proposal />} />
                        <Route path="/login" element={<Login />} />
                        <Route element={<AdminRoutes />}>
                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/playground" element={<Playground />} />
                            <Route path="/train" element={<TrainAgent />} />
                            <Route path="/live-chats" element={<LiveChats />} />

                            {/* Activity */}
                            <Route path="/activity/chats" element={<Conversations />} />
                            <Route path="/activity/leads" element={<Leads />} />
                            <Route path="/activity/tickets" element={<Tickets />} />
                            <Route path="/conversations" element={<Navigate to="/activity/chats" replace />} />
                            <Route path="/leads" element={<Navigate to="/activity/leads" replace />} />
                            <Route path="/tickets" element={<Navigate to="/activity/tickets" replace />} />

                            {/* Analytics */}
                            <Route path="/analytics/chats" element={<AnalyticsChats />} />
                            <Route path="/analytics/topics" element={<Topics />} />
                            <Route path="/analytics/sentiment" element={<Sentiment />} />

                            {/* Data sources */}
                            <Route path="/data-sources/files" element={<Documents />} />
                            <Route path="/data-sources/snippets" element={<Snippets />} />
                            <Route path="/data-sources/website" element={<Websites />} />
                            <Route path="/data-sources/qa" element={<QA />} />
                            <Route path="/data-sources/notion" element={<DataSourceNotion />} />
                            <Route path="/data-sources/tickets" element={<DataSourceTickets />} />
                            <Route path="/data-sources/suggestions" element={<Suggestions />} />
                            <Route path="/documents" element={<Navigate to="/data-sources/files" replace />} />
                            <Route path="/qa" element={<Navigate to="/data-sources/qa" replace />} />
                            <Route path="/snippets" element={<Navigate to="/data-sources/snippets" replace />} />
                            <Route path="/websites" element={<Navigate to="/data-sources/website" replace />} />
                            <Route path="/suggestions" element={<Navigate to="/data-sources/suggestions" replace />} />

                            {/* Actions / Widgets / Contacts / Deploy / Outbound */}
                            <Route path="/actions" element={<Actions />} />
                            <Route path="/widgets" element={<Widgets />} />
                            <Route path="/contacts" element={<Contacts />} />
                            <Route path="/deploy" element={<Deploy />} />
                            <Route path="/outbound" element={<Outbound />} />
                            <Route path="/deployment" element={<DeploymentGuide />} />

                            {/* Help desk */}
                            <Route path="/helpdesk/email" element={<HelpdeskEmail />} />
                            <Route path="/helpdesk/assignment" element={<HelpdeskAssignment />} />
                            <Route path="/helpdesk/scheduling" element={<HelpdeskScheduling />} />
                            <Route path="/helpdesk/translation" element={<HelpdeskTranslation />} />

                            {/* Settings */}
                            <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
                            <Route path="/settings/general" element={<SettingsGeneral />} />
                            <Route path="/settings/ai" element={<SettingsAI />} />
                            <Route path="/settings/voice" element={<SettingsVoice />} />
                            <Route path="/settings/integrations" element={<SettingsIntegrations />} />
                            <Route path="/settings/fundle" element={<SettingsFundle />} />
                            <Route path="/settings/lead-email" element={<SettingsLeadEmail />} />
                            <Route path="/settings/agents" element={<SettingsAgents />} />
                            <Route path="/settings/canned" element={<SettingsCannedReplies />} />
                            <Route path="/settings/users" element={<SettingsUsers />} />
                            <Route path="/settings/emails" element={<SettingsEmails />} />
                            <Route path="/settings/chat-interface" element={<SettingsChatInterface />} />
                            <Route path="/settings/security" element={<SettingsSecurity />} />
                            <Route path="/settings/custom-domains" element={<SettingsCustomDomains />} />
                            <Route path="/settings/webhooks" element={<SettingsWebhooks />} />
                            <Route path="/settings/notifications" element={<SettingsNotifications />} />
                            <Route path="/settings/fundle" element={<LegacySettings />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
